import {TrackingContextAppend} from '../TrackingContext.jsx';
import React from 'react';
import {CfPreview} from '../CfPreviewContext.jsx';
import {HalfSizeTeaser} from './HalfSizeTeaser.jsx';
import {HeroTeaser} from './HeroTeaser.jsx';
import {ErrorTeaser} from './ErrorTeaser.jsx';
import {useIntersection} from "@teaser/scripts/components/article-overview-teaser-content/utils.jsx";
import {TrinityEvents as Trinity} from "@teaser/scripts/modules/teaser/trinity.js";
import {ProductSlider} from "@teaser/scripts/components/article-overview-teaser-content/ProductSlider.jsx";
import {TextTeaser} from "@teaser/scripts/components/article-overview-teaser-content/TextTeaser.jsx";
import {TopicCarousel} from "@teaser/scripts/components/article-overview-teaser-content/TopicCarousel.jsx";
import {XRay} from "@teaser/scripts/components/XRay.jsx";
import {SaleTeaser} from "@teaser/scripts/components/article-overview-teaser-content/SaleTeaser.jsx";
import {BenefitCommunication} from './BenefitCommunication.jsx';
import {TextProvider} from '@teaser/scripts/components/Text.jsx';
import {PortraitGridTeaser} from './PortraitGridTeaser.jsx';
import {LastSeenProductsContainerTeaser} from './LastSeenProductsContainerTeaser.jsx';
import {OutfitInspiration} from './OutfitInspiration.jsx';
import {CountdownTeaser} from './CountdownTeaser.jsx';
import {FlowboxTeaser} from './FlowboxTeaser.jsx';

/**
 * @typedef {Object} ArticleOverviewGridTeaserModel
 * @property {HalfSizeTeaserModel | HeroTeaserModel | ProductSliderModel | TextTeaserModel | TopicCarouselModel | SaleTeaserModel | OutfitInspiration | LastSeenProductsContainerModel | ErrorTeaserModel} teaser
 * @property {number} position
 * @property {number} size
 */

/**
 * @param {ArticleOverviewTeaserContentModel} pageModel
 * @param {ArticleOverviewGridTeaserModel} teaserModel
 * @returns {JSX.Element}
 */
export function ArticleOverviewGridTeaser({pageModel, teaserModel}) {
    if (!pageModel || !teaserModel) return null;

    const landmark =
        pageModel.tracking
            ? {
                cfEnvironment: pageModel.tracking.environment,
                teaserPageEntryId: pageModel.tracking.entryId,
                shopId: pageModel.shopIdentifier.shopId,
                functionId: pageModel.tracking.trackingData.functionId,
                page: 'aop'
            }
            : null

    const triggerRef = React.useRef(null);
    const hasTracked = React.useRef(false);
    const isVisible = useIntersection(triggerRef, "0px");
    
    React.useEffect(() => {
        if (isVisible && !hasTracked.current) {
            hasTracked.current = true;
            Trinity.sendViewTeaserElement({
                teaserElementId: teaserModel.teaser.entryId,
                page: 'aop',
                teaserType: teaserModel.teaser.contentType,
                shopId: pageModel.shopIdentifier.shopId,
                functionId: pageModel.tracking.trackingData.functionId
            });
        }
    }, [isVisible, teaserModel.teaser.entryId, teaserModel.teaser.contentType, pageModel.shopIdentifier.shopId, pageModel.tracking.trackingData.functionId]);

    const innerTeaser = <Teaser model={teaserModel.teaser} device={pageModel.device} key={teaserModel.teaser.entryId}
                                ref={triggerRef}/>
    return (
        <TrackingContextAppend value={landmark}>
            <CfPreview
                enabled={pageModel.enablePreview}
                space={pageModel.space}
                environment={landmark.cfEnvironment}
                xrayModeEnabled={pageModel.xrayModeEnabled}
            >
                <TextProvider texts={pageModel.internationalTexts}>
                    {pageModel.xrayModeEnabled ? (
                            <div className="contentful-xray-mode-enabled">
                                <div className="contentful-xray-mode-page-label-container">
                                    <XRay
                                        contentType={pageModel.contentType}
                                        fieldId="gridTeasers"
                                        entryId={landmark.teaserPageEntryId}
                                    />
                                </div>
                                {innerTeaser}
                            </div>)
                        : innerTeaser
                    }
                </TextProvider>
            </CfPreview>
        </TrackingContextAppend>
    );
}

/**
 * @param {HalfSizeTeaserModel | HeroTeaserModel | ProductSliderModel | TextTeaserModel | TopicCarouselModel | SaleTeaserModel | OutfitInspiration | LastSeenProductsContainerModel | ErrorTeaserModel} model
 * @param {Device} device
 * @param {Ref<HTMLDivElement> | null} ref
 * @returns {JSX.Element}
 */
const Teaser = React.forwardRef(function Teaser({model, device}, ref) {
    switch (model.type) {
        case 'PRODUCT_SLIDER':
            return <ProductSlider model={model} device={device}/>;
        case 'TEXT_TEASER':
            return <TextTeaser model={model} device={device}/>;
        case 'HALF_SIZE_TEASER':
            return <HalfSizeTeaser model={model} ref={ref}/>;
        case 'PORTRAIT_GRID_TEASER':
            return <PortraitGridTeaser model={model} ref={ref}/>;
        case 'HERO_TEASER':
            return <HeroTeaser model={model} device={device} ref={ref} disableViewTracking={true}/>;
        case 'TOPIC_CAROUSEL':
        case 'PERSO_TOPIC_CAROUSEL':
            return <TopicCarousel model={model} device={device}/>;
        case 'SALE_TEASER':
            return <SaleTeaser model={model} device={device}/>;
        case 'OUTFIT_INSPIRATION':
            return <OutfitInspiration model={model} device={device}/>;
        case 'BENEFIT_COMMUNICATION':
            return <BenefitCommunication model={model}/>;
        case 'ERROR_TEASER':
            return <ErrorTeaser model={model}/>;
        case 'COUNTDOWN_TEASER':
            return <CountdownTeaser model={model}/>;
        case 'FLOWBOX_TEASER':
            return <FlowboxTeaser model={model}/>;
        case 'LAST_SEEN_PRODUCTS_CONTAINER':
            // LSP has its own view-tracking; no ref needed for the outer intersection observer.
            return <LastSeenProductsContainerTeaser />;
        default:
            return null;
    }
});
